import { Node, Edge, NetworkStats } from "./interface";

interface NetworkConfig {
    initialNodes?: number;
    minCapacity?: number;
    maxCapacity?: number;
    shedPercent?: number;
    m?: number;
}

export class Network {
    nodes: Node[];
    edges: Edge[];
    time: number;
    minCapacity: number;
    maxCapacity: number;
    shedPercent: number;
    m: number;
    constructor({
        initialNodes = 5,
        minCapacity = 3,
        maxCapacity = 20,
        shedPercent = 0.5,
        m = 5,
    }: NetworkConfig = {}) {
        this.nodes = [];
        this.edges = [];
        this.time = 0;
        this.minCapacity = minCapacity;
        this.maxCapacity = maxCapacity;
        this.shedPercent = shedPercent;
        this.m = m;

        // Initialize with a complete graph
        for (let i = 0; i < initialNodes; i++) {
            this.addNode();
        }
        for (let i = 0; i < initialNodes; i++) {
            for (let j = i + 1; j < initialNodes; j++) {
                this.addEdge(i, j);
            }
        }
    }

    addNode(): number {
        const capacity =
            Math.floor(
                Math.random() * (this.maxCapacity - this.minCapacity + 1)
            ) + this.minCapacity;
        this.nodes.push({
            id: this.nodes.length,
            connections: [],
            degree: 0,
            capacity: capacity,
        });
        return this.nodes.length - 1;
    }

    addEdge(source: number, target: number): boolean {
        if (source === target) return false;

        const sourceNode = this.nodes[source];
        const targetNode = this.nodes[target];

        if (sourceNode.connections.includes(target)) return false;

        sourceNode.connections.push(target);
        targetNode.connections.push(source);

        sourceNode.degree++;
        targetNode.degree++;

        this.edges.push({ source, target });

        return this.shed(source) || this.shed(target);
    }

    shed(nodeIndex: number): boolean {
        const node = this.nodes[nodeIndex];

        if (node.degree > node.capacity) {
            const edgesToShed = Math.ceil(
                node.connections.length * this.shedPercent
            );

            const shuffledConnections = [...node.connections].sort(
                () => 0.5 - Math.random()
            );
            const connectionsToRemove = shuffledConnections.slice(
                0,
                edgesToShed
            );

            for (const targetIndex of connectionsToRemove) {
                // Remove the edge
                this.removeEdge(nodeIndex, targetIndex);

                if (node.connections.length > 0) {
                    const randomNeighborIndex = Math.floor(
                        Math.random() * node.connections.length
                    );
                    const randomNeighbor =
                        node.connections[randomNeighborIndex];

                    if (randomNeighbor !== targetIndex) {
                        this.addEdge(randomNeighbor, targetIndex);
                        return true;
                    } else {
                        // NOTE: this should not happen
                        console.error("No neighbors to reassign edge to");
                    }
                }
            }
        }

        return false;
    }

    removeEdge(source: number, target: number): void {
        const sourceNode = this.nodes[source];
        const targetNode = this.nodes[target];

        sourceNode.connections = sourceNode.connections.filter(
            (conn) => conn !== target
        );
        targetNode.connections = targetNode.connections.filter(
            (conn) => conn !== source
        );

        sourceNode.degree--;
        targetNode.degree--;

        this.edges = this.edges.filter(
            (edge) =>
                !(edge.source === source && edge.target === target) &&
                !(edge.source === target && edge.target === source)
        );
    }

    preferentialAttachment(newNodeIndex: number): boolean {
        let totalDegree = this.getTotalDegree();
        if (totalDegree === 0) totalDegree = 1; // Avoid division by zero

        for (let i = 0; i < this.m; i++) {
            let r = Math.random() * totalDegree;
            let cumulativeDegree = 0;

            for (let j = 0; j < newNodeIndex; j++) {
                cumulativeDegree += this.nodes[j].degree;
                if (r <= cumulativeDegree) {
                    return this.addEdge(newNodeIndex, j);
                }
            }
        }

        return false;
    }

    getTotalDegree(): number {
        return this.nodes.reduce((sum, node) => sum + node.degree, 0);
    }

    simulate(): { nodes: Node[]; edges: Edge[]; didShed: boolean } {
        const newNodeIndex = this.addNode();
        const didShed = this.preferentialAttachment(newNodeIndex);

        return {
            nodes: this.nodes,
            edges: this.edges,
            didShed,
        };
    }

    getStatistics(): NetworkStats {
        const degrees = this.nodes.map((node) => node.degree);
        const avgDegree =
            degrees.reduce((sum, degree) => sum + degree, 0) /
            this.nodes.length;

        const capacities = this.nodes.map((node) => node.capacity);
        const avgCapacity =
            capacities.reduce((sum, capacity) => sum + capacity, 0) /
            this.nodes.length;

        // Count nodes at capacity
        const nodesAtCapacity = this.nodes.filter(
            (node) => node.degree >= node.capacity
        ).length;

        return {
            nodeCount: this.nodes.length,
            edgeCount: this.edges.length,
            averageDegree: avgDegree,
            averageCapacity: avgCapacity,
            nodesAtCapacity,
            percentAtCapacity: (nodesAtCapacity / this.nodes.length) * 100,
        };
    }

    getDegreeDistribution() {
        const maxDegree = Math.max(...this.nodes.map((node) => node.degree));
        const distribution = new Array(maxDegree + 1).fill(0);

        this.nodes.forEach((node) => {
            distribution[node.degree]++;
        });

        // Convert to probability
        const probabilityDistribution = distribution.map(
            (count) => count / this.nodes.length
        );

        return {
            degrees: Array.from({ length: maxDegree + 1 }, (_, i) => i),
            probabilities: probabilityDistribution,
        };
    }

    clone(): Network {
        // Create a new instance with the same configuration
        const networkCopy = new Network({
            initialNodes: this.nodes.length,
            minCapacity: this.minCapacity,
            maxCapacity: this.maxCapacity,
            shedPercent: this.shedPercent,
            m: this.m,
        });

        // Set the time
        networkCopy.time = this.time;

        // Deep copy the nodes
        networkCopy.nodes = this.nodes.map((node) => ({
            id: node.id,
            connections: [...node.connections],
            degree: node.degree,
            capacity: node.capacity,
        }));

        // Deep copy the edges
        networkCopy.edges = this.edges.map((edge) => ({
            source: edge.source,
            target: edge.target,
        }));

        return networkCopy;
    }
}
